import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCategoryOptions } from '@/features/categories';
import { SearchInput } from '@/shared/components/search-input';
import type { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

/**
 * Filter controls for the brands table.
 *
 * All state lives in the URL via `tableState`, so this component holds none of
 * its own — it reads and writes search params and nothing else (§8).
 *
 * Radix Select cannot hold an empty-string value, so "any" is modelled as the
 * sentinel `'all'` and translated to `null` (absent from the URL) on the way
 * out.
 */
const ANY = 'all';

export function BrandsFilters({
  tableState,
}: {
  tableState: ReturnType<typeof useTableSearchParams>;
}) {
  const categories = useCategoryOptions();

  const status = tableState.getFilter('status') ?? ANY;
  const categoryId = tableState.getFilter('categoryId') ?? ANY;

  return (
    <>
      <SearchInput
        value={tableState.search}
        onChange={tableState.setSearch}
        placeholder="Search brands…"
        className="w-full sm:w-60"
      />

      <Select
        value={status}
        onValueChange={(value) => tableState.setFilter('status', value === ANY ? null : value)}
      >
        <SelectTrigger size="sm" className="h-8 w-32 text-xs" aria-label="Filter by status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY} className="text-xs">
            Any status
          </SelectItem>
          <SelectItem value="active" className="text-xs">
            Active
          </SelectItem>
          <SelectItem value="inactive" className="text-xs">
            Inactive
          </SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={categoryId}
        onValueChange={(value) => tableState.setFilter('categoryId', value === ANY ? null : value)}
      >
        <SelectTrigger size="sm" className="h-8 w-40 text-xs" aria-label="Filter by category">
          <SelectValue placeholder="Any category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY} className="text-xs">
            Any category
          </SelectItem>
          {categories.data?.map((category) => (
            <SelectItem key={category.id} value={String(category.id)} className="text-xs">
              {category.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}
